import { useCallback, useEffect, useState } from "react"
import { Route as RouteIcon, RefreshCw } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { useReadOnly } from "@/lib/read-only"
import {
  fetchOnboardingPathMetrics,
  fetchOnboardingPaths,
  saveOnboardingPath,
  GATE_IDS,
  OnboardingPathSaveError,
  type OnboardingMetric,
  type OnboardingMetricsResponse,
  type OnboardingPathsResponse,
  type OnboardingPathRow,
} from "@/lib/onboarding-paths-api"
import { cn } from "@/lib/utils"

const gateSet = new Set<string>(GATE_IDS)

const WINDOWS = [7, 30, 90] as const

const TILES: { key: keyof Omit<OnboardingMetricsResponse["metrics"], "path_opt_in">; label: string; target: string }[] = [
  { key: "time_to_aha", label: "Time to first a-ha", target: "<= 5 min" },
  { key: "first_reply_rate", label: "First-reply rate", target: ">= 65%" },
  { key: "aha_reaction", label: "A-ha reaction", target: ">= 40%" },
  { key: "first_session_booking", label: "First-session booking", target: ">= 25%" },
  { key: "drop_off", label: "Drop-off", target: "<= 30%" },
  { key: "card_24h", label: "Card in 24h", target: ">= 20%" },
  { key: "d1", label: "D1", target: ">= 45%" },
  { key: "d7", label: "D7", target: ">= 30%" },
  { key: "d30", label: "D30", target: ">= 15%" },
]

function formatRate(metric: OnboardingMetric): string {
  if (metric.unknown || metric.value == null) return "unknown"
  return `${Math.round(metric.value * 100)}% (${metric.numerator}/${metric.denominator})`
}

function Toggle({
  on,
  disabled,
  onClick,
}: {
  on: boolean
  disabled: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-md border px-2.5 py-1 text-xs font-medium min-w-[3rem]",
        on ? "bg-primary text-primary-foreground" : "hover:bg-accent",
        disabled && "opacity-50",
      )}
    >
      {on ? "On" : "Off"}
    </button>
  )
}

export function OnboardingPathsPage() {
  const { readOnly } = useReadOnly()
  const [data, setData] = useState<OnboardingPathsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [rankDrafts, setRankDrafts] = useState<Record<string, string>>({})
  const [metricDays, setMetricDays] = useState<number>(30)
  const [metrics, setMetrics] = useState<OnboardingMetricsResponse | null>(null)
  const [metricsError, setMetricsError] = useState(false)

  const loadMetrics = useCallback(async (days: number) => {
    setMetricsError(false)
    try {
      setMetrics(await fetchOnboardingPathMetrics(days))
    } catch {
      setMetrics(null)
      setMetricsError(true)
    }
  }, [])

  useEffect(() => {
    void loadMetrics(metricDays)
  }, [loadMetrics, metricDays])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const next = await fetchOnboardingPaths()
      setData(next)
      const drafts: Record<string, string> = {}
      for (const id of next.ids) {
        if (gateSet.has(id)) continue
        const rank = next.rows.find((r) => r.path_id === id)?.rank
        if (rank != null) drafts[id] = String(rank)
      }
      setRankDrafts(drafts)
    } catch {
      setData(null)
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const runSave = async (pathId: string, body: Parameters<typeof saveOnboardingPath>[1]) => {
    setNotice(null)
    setSavingId(pathId)
    try {
      await saveOnboardingPath(pathId, body)
    } catch (e) {
      const err = e instanceof OnboardingPathSaveError ? e : null
      setNotice(
        err?.status === 409
          ? "Changed elsewhere, reloaded"
          : err?.status === 404
            ? "Row missing, reloaded"
            : err?.status === 400
              ? "Not saved: invalid value"
              : "Save failed",
      )
    } finally {
      setSavingId(null)
      await load()
    }
  }

  const rowFor = (id: string) => data?.rows.find((r) => r.path_id === id)

  const commitRank = (id: string, row: OnboardingPathRow) => {
    const parsed = Number(rankDrafts[id] ?? "")
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 99) {
      setNotice("Rank must be 1 to 99")
      setRankDrafts((d) => ({ ...d, [id]: row.rank != null ? String(row.rank) : "" }))
      return
    }
    if (parsed !== row.rank) {
      void runSave(id, { enabled: row.enabled, rank: parsed, expected_updated_at: row.updated_at })
    }
  }

  const pathIds = data?.ids.filter((id) => !gateSet.has(id)) ?? []
  const gateIds = data?.ids.filter((id) => gateSet.has(id)) ?? []

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <RouteIcon className="size-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold">Onboarding paths</h1>
          <p className="text-sm text-muted-foreground">
            The agent reads these switches on each member&apos;s next turn.
          </p>
        </div>
      </div>
      {loading && !data && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      )}
      {loadError && !loading && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-center justify-between gap-3">
          <span className="text-sm text-destructive">Could not load switches</span>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
          >
            <RefreshCw className="size-4" />
            Reload
          </button>
        </div>
      )}
      {data && !loadError && (
        <>
          {notice && (
            <div className="rounded-lg border bg-muted/50 p-3 text-sm">{notice}</div>
          )}
          <section className="rounded-lg border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Path</th>
                  <th className="px-4 py-2">Enabled</th>
                  <th className="px-4 py-2">Rank</th>
                  <th className="px-4 py-2">Updated by</th>
                  <th className="px-4 py-2">Updated at</th>
                </tr>
              </thead>
              <tbody>
                {pathIds.map((id) => {
                  const row = rowFor(id)
                  const busy = savingId === id
                  const disabled = readOnly || busy
                  if (!row) {
                    return (
                      <tr key={id} className="border-b last:border-0">
                        <td className="px-4 py-3 font-mono text-xs">{id}</td>
                        <td colSpan={4} className="px-4 py-3 text-muted-foreground">
                          missing (treated as off by the agent)
                        </td>
                      </tr>
                    )
                  }
                  return (
                    <tr key={id} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs">{id}</div>
                        {id === "socials" && !data.connect_tools.socials && (
                          <div className="text-xs text-muted-foreground mt-0.5">no connect tool yet</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Toggle
                          on={row.enabled}
                          disabled={disabled}
                          onClick={() =>
                            void runSave(id, {
                              enabled: !row.enabled,
                              rank: row.rank,
                              expected_updated_at: row.updated_at,
                            })
                          }
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min={1}
                          max={99}
                          disabled={disabled}
                          value={rankDrafts[id] ?? (row.rank != null ? String(row.rank) : "")}
                          onChange={(e) => setRankDrafts((d) => ({ ...d, [id]: e.target.value }))}
                          onBlur={() => commitRank(id, row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault()
                              commitRank(id, row)
                            }
                          }}
                          className="w-16 rounded-md border px-2 py-1 text-sm disabled:opacity-50"
                        />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{row.updated_by}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(row.updated_at).toLocaleString()}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </section>
          {gateIds.length > 0 && (
            <section className="rounded-lg border bg-card p-4 space-y-3">
              <h2 className="text-sm font-semibold">Gmail read gate</h2>
              {gateIds.map((id) => {
                const row = rowFor(id)
                const disabled = readOnly || savingId === id
                if (!row) {
                  return (
                    <p key={id} className="text-sm text-muted-foreground">
                      <span className="font-mono text-xs text-foreground">{id}</span> missing (treated as off by
                      the agent)
                    </p>
                  )
                }
                return (
                  <div key={id} className="flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-mono text-xs">{id}</span>
                    <Toggle
                      on={row.enabled}
                      disabled={disabled}
                      onClick={() =>
                        void runSave(id, {
                          enabled: !row.enabled,
                          rank: null,
                          expected_updated_at: row.updated_at,
                        })
                      }
                    />
                    <span className="text-muted-foreground text-xs">
                      {row.updated_by} · {new Date(row.updated_at).toLocaleString()}
                    </span>
                  </div>
                )
              })}
            </section>
          )}
        </>
      )}
      <section className="rounded-lg border bg-card p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-sm font-semibold">PRD metrics</h2>
              <div className="flex gap-2">
                {WINDOWS.map((days) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setMetricDays(days)}
                    className={cn(
                      "rounded-md border px-2.5 py-1 text-xs font-medium",
                      metricDays === days ? "bg-primary text-primary-foreground" : "hover:bg-accent",
                    )}
                  >
                    {days}d
                  </button>
                ))}
              </div>
            </div>
            {metricsError && (
              <div className="flex items-center justify-between gap-3 text-sm text-destructive">
                <span>Could not load metrics</span>
                <button
                  type="button"
                  onClick={() => void loadMetrics(metricDays)}
                  className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-accent"
                >
                  <RefreshCw className="size-4" />
                  Reload
                </button>
              </div>
            )}
            {metrics && (
              <>
                <p className="text-xs text-muted-foreground">
                  Cohort {metrics.cohort_size ?? "unknown"}
                  {metrics.truncated ? " · read stopped at the row cap" : ""}
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {TILES.map((tile) => {
                    const metric = metrics.metrics[tile.key]
                    return (
                      <div key={tile.key} className="rounded-md border p-3 space-y-1">
                        <div className="text-xs text-muted-foreground">{tile.label}</div>
                        <div className="text-lg font-semibold">{formatRate(metric)}</div>
                        <div className="text-xs text-muted-foreground">Target {tile.target}</div>
                        {tile.key === "time_to_aha" && !metric.unknown && (
                          <div className="text-xs text-muted-foreground">
                            Median {metric.median_seconds == null ? "unknown" : `${Math.round(metric.median_seconds / 60)} min`}
                            {" · "}no a-ha yet {metric.no_aha_yet ?? "unknown"}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="rounded-md border p-3 space-y-2">
                  <div className="text-xs text-muted-foreground">Path opt-in · target &gt;= 50%</div>
                  {metrics.metrics.path_opt_in.unknown && <div className="text-sm">unknown</div>}
                  {!metrics.metrics.path_opt_in.unknown &&
                    Object.entries(metrics.metrics.path_opt_in.paths).map(([path, metric]) => (
                      <div key={path} className="flex justify-between text-sm">
                        <span className="font-mono text-xs">{path}</span>
                        <span>{formatRate(metric)}</span>
                      </div>
                    ))}
                </div>
                <div className="rounded-md border p-3 space-y-2">
                  <div className="text-xs text-muted-foreground">PostHog</div>
                  {metrics.posthog_error && <div className="text-sm">PostHog unavailable</div>}
                  {metrics.posthog?.map((row) => (
                    <div key={row.event} className="flex justify-between text-sm">
                      <span className="font-mono text-xs">{row.event}</span>
                      <span>{row.count} events · {row.people} people</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
    </div>
  )
}
